import React, { useState, useEffect, useCallback } from 'react';
import cashflowService from '../services/cashflowService';
import sharepointService from '../services/sharepointService';
import clientHelperService from '../services/clientHelperService';

/**
 * Modal that publishes a client's cashflow Excel to SharePoint and adds it
 * to the matching knowledgebase client.
 *
 * Flow:
 *  1.  Advisor selects the knowledgebase client (pre-filled if already linked).
 *  2.  SharePoint folders for that client are loaded automatically.
 *  3.  Advisor selects (or accepts the suggested) destination folder.
 *  4.  Advisor confirms the filename.
 *  5.  On submit:
 *        a. Export Excel from cashflow service.
 *        b. Upload the file to the SharePoint service staging bucket.
 *        c. Set the accepted filename.
 *        d. Approve & push to SharePoint + knowledgebase.
 *
 * @param {object} client    - Cashflow ClientListItem (includes name, client_id,
 *                             knowledgebase_client_id).
 * @param {function} onClose - Called when the modal should be dismissed.
 * @param {function} onSuccess - Called with the approve-file-and-summary result.
 */
export default function CashflowSharePointModal({ client, onClose, onSuccess }) {
  const currentYear = new Date().getFullYear();

  // Derive a "Last Name" for the default filename.
  // client.name is expected in "Last, First" format; fall back to last word.
  const lastName = client.name.includes(',')
    ? client.name.split(',')[0].trim()
    : client.name.trim().split(' ').pop();

  const defaultFilename = `Cashflow-Annual Cashflow Analysis-${lastName}-${currentYear}.xlsx`;

  // ---------------- state ----------------
  const [spClients, setSpClients] = useState([]);
  const [spClient, setSpClient] = useState('');
  const [newSpClientName, setNewSpClientName] = useState('');

  const [kbClients, setKbClients] = useState([]);
  const [kbClientId, setKbClientId] = useState('');
  const [kbClientName, setKbClientName] = useState('');
  const [newKbClientName, setNewKbClientName] = useState('');

  const [folders, setFolders] = useState([]);
  const [folderPath, setFolderPath] = useState('');
  const [loadingFolders, setLoadingFolders] = useState(false);

  const [filename, setFilename] = useState(defaultFilename);
  const [overwrite, setOverwrite] = useState(false);

  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');

  // ---------------- data loading ----------------

  const loadFolders = useCallback(async (clientName) => {
    if (!clientName) return;
    setLoadingFolders(true);
    setFolders([]);
    setFolderPath('');
    try {
      const result = await sharepointService.getClientFolders(clientName);
      let folderList = result.folders || [];

      // Root entry (name === "/") holds the client's base full_path in SharePoint.
      const rootEntry = folderList.find(f => f.name === '/');
      const rootFullPath = rootEntry?.full_path || clientName;

      // Look for an "FP Docs" folder (case-insensitive exact match on relative name).
      let fpDocsFolder = folderList.find(f =>
        f.name?.toLowerCase() === 'fp docs'
      );

      // If it doesn't exist yet, create it under the client root.
      if (!fpDocsFolder) {
        try {
          const created = await sharepointService.createFolder('FP Docs', rootFullPath, clientName, true);
          const createdFolder = created?.folder;   // response is { success, folder: { id, full_path, ... } }
          if (createdFolder?.full_path) {
            fpDocsFolder = {
              id: createdFolder.id,
              name: 'FP Docs',
              full_path: createdFolder.full_path,
              depth: 1,
            };
            folderList = [...folderList, fpDocsFolder];
          }
        } catch (e) {
          console.warn('Could not create FP Docs folder:', e);
        }
      }

      // Hide the synthetic root "/" entry from the dropdown.
      setFolders(folderList.filter(f => f.name !== '/'));

      let resolvedPath = '';
      if (fpDocsFolder) {
        resolvedPath = fpDocsFolder.full_path;
      } else if (folderList.length > 0) {
        const first = folderList.find(f => f.name !== '/') || folderList[0];
        resolvedPath = first.full_path;
      }
      setFolderPath(resolvedPath);
      return resolvedPath;
    } catch (e) {
      console.error('Failed to load folders:', e);
      setFolders([]);
      return '';
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clients, spFolders] = await Promise.all([
          clientHelperService.fetchClients().catch(() => []),
          sharepointService.getClients().catch(() => []),
        ]);
        setKbClients(clients);
        setSpClients(spFolders);

        // Preselect KB client by explicit ID or name match.
        let matchedKbId = '';
        let matchedKbName = '';
        if (client.knowledgebase_client_id) {
          const m = clients.find(c => c.client_id === client.knowledgebase_client_id);
          if (m) { matchedKbId = m.client_id; matchedKbName = m.client_name; }
        }
        if (!matchedKbId) {
          const normalize = s => s?.toLowerCase().replace(/[^a-z]/g, '') || '';
          const parts = client.name.includes(',')
            ? client.name.split(',').map(s => s.trim())
            : client.name.trim().split(/\s+/).reverse();
          const [last, first] = parts;
          const candidates = [normalize(`${last}${first}`), normalize(`${first}${last}`)].filter(Boolean);
          const m = clients.find(c => candidates.some(cand => normalize(c.client_name) === cand));
          if (m) { matchedKbId = m.client_id; matchedKbName = m.client_name; }
        }
        if (matchedKbId) { setKbClientId(matchedKbId); setKbClientName(matchedKbName); }

        // Preselect SP client folder from KB name or cashflow client name.
        const nameToSearch = matchedKbName || client.name;
        const normSearch = nameToSearch.replace(/_REBELLIONAIRE/gi, '').toLowerCase().trim();
        const spMatch =
          spFolders.find(f => f.toLowerCase().trim() === normSearch) ||
          spFolders.find(f => f.toLowerCase().includes(normSearch) || normSearch.includes(f.toLowerCase()));
        if (spMatch) setSpClient(spMatch);
      } catch (e) {
        console.error('Failed to load client data:', e);
      }
    };
    loadData();
  }, [client.knowledgebase_client_id, client.name]);

  useEffect(() => {
    if (spClient && spClient !== '__new__') loadFolders(spClient);
    else { setFolders([]); setFolderPath(''); }
  }, [spClient, loadFolders]);

  // ---------------- handlers ----------------

  const handleSpClientChange = (value) => {
    setSpClient(value);
    setNewSpClientName('');
    if (value && value !== '__new__') {
      const normalized = value.replace(/_REBELLIONAIRE/gi, '').toLowerCase().trim();
      let match = kbClients.find(c => c.client_name.toLowerCase().trim() === normalized);
      if (!match) match = kbClients.find(c =>
        c.client_name.toLowerCase().includes(normalized) ||
        normalized.includes(c.client_name.toLowerCase())
      );
      if (!match) match = kbClients.find(c =>
        c.nicknames?.some(n =>
          n.toLowerCase().trim() === normalized ||
          n.toLowerCase().includes(normalized) ||
          normalized.includes(n.toLowerCase())
        )
      );
      setKbClientId(match?.client_id || '');
      setKbClientName(match?.client_name || '');
      setNewKbClientName('');
    } else if (!value) {
      setKbClientId('');
      setKbClientName('');
      setNewKbClientName('');
    }
  };

  const handleKbClientChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId === '__new__') {
      setKbClientId('__new__');
      setKbClientName('');
      const suggestion = client.name.includes(',')
        ? client.name.trim()
        : client.name.trim().split(/\s+/).reverse().join(', ');
      setNewKbClientName(suggestion);
      return;
    }
    const selected = kbClients.find(c => c.client_id === selectedId);
    setKbClientId(selectedId);
    setKbClientName(selected?.client_name || '');
    setNewKbClientName('');
  };

  const handlePublish = async () => {
    // Resolve SP folder name and KB client — may need to create one or both.
    const resolvedSpFolder = spClient === '__new__' ? newSpClientName.trim() : spClient;
    let resolvedKbClientId = kbClientId;
    let resolvedKbClientName = kbClientName;

    if (!resolvedSpFolder) {
      setError('Please select or create a SharePoint folder.');
      return;
    }
    if (kbClientId === '__new__') {
      const kbName = newKbClientName.trim();
      if (!kbName) {
        setError('Please enter a name for the new knowledgebase client.');
        return;
      }
      resolvedKbClientName = kbName;
    } else if (!kbClientId) {
      setError('Please select or create a knowledgebase client.');
      return;
    }
    if (!folderPath && spClient !== '__new__') {
      setError('Please select a SharePoint destination folder.');
      return;
    }
    if (!filename.trim()) {
      setError('Filename cannot be empty.');
      return;
    }

    setLoading(true);
    setError('');

    let resolvedFolderPath = folderPath;

    try {
      // Step 0a — Create new SharePoint client folder if needed.
      if (spClient === '__new__') {
        setStatusMsg('Creating SharePoint client folder…');
        try {
          await sharepointService.createClient(resolvedSpFolder);
        } catch (spErr) {
          console.warn('SharePoint folder creation failed (may already exist):', spErr);
        }
        resolvedFolderPath = await loadFolders(resolvedSpFolder);
        if (!resolvedFolderPath) {
          throw new Error('SharePoint folder could not be located. Please check SharePoint and try again.');
        }
      }

      // Step 0b — Create new KB client if needed.
      if (kbClientId === '__new__') {
        setStatusMsg('Creating knowledgebase client…');
        const kbResult = await clientHelperService.createKbClient({ client_name: resolvedKbClientName });
        resolvedKbClientId = kbResult.client_id;
      }

      const effectiveFolderPath = resolvedFolderPath;
      if (!effectiveFolderPath) {
        throw new Error('No destination folder available. Please select a folder and try again.');
      }

      // Step 1 — Generate Excel from cashflow service.
      setStatusMsg('Generating Excel spreadsheet…');
      const excelResp = await cashflowService.exportClientExcel(client.client_id);
      const excelBlob = new Blob([excelResp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const excelFile = new File([excelBlob], filename, { type: excelBlob.type });

      // Step 2 — Upload to SharePoint service staging bucket.
      setStatusMsg('Uploading to staging…');
      const uploadResult = await sharepointService.uploadFile(
        excelFile,
        resolvedSpFolder,
        resolvedKbClientId,
        null,
        { skipAnalysis: true, skipRedaction: true },
      );
      const fileId = uploadResult.fileId;
      if (!fileId) throw new Error('Upload failed — no fileId returned.');

      // Step 3 — Set the accepted (display) filename.
      setStatusMsg('Setting filename…');
      await sharepointService.updateFileName(fileId, filename, resolvedSpFolder);

      // Step 4 — Approve & push to SharePoint + knowledgebase.
      setStatusMsg('Publishing to SharePoint and knowledgebase…');
      const result = await sharepointService.approveFileAndSummary(
        fileId,
        resolvedSpFolder,
        resolvedKbClientId,
        '',                     // folderId — not required; folderPath is used
        effectiveFolderPath,
        overwrite,
        'Mixed',
        'Mixed',
        'NONE',
      );

      if (result.success) {
        onSuccess(result);
      } else if (result.error === 'FILE_EXISTS') {
        setError(
          'A file with this name already exists in the selected folder. ' +
          'Enable "Overwrite existing file" to replace it.',
        );
      } else {
        setError(result.error || result.message || 'Publish failed. Please try again.');
      }
    } catch (e) {
      console.error('Publish error:', e);
      setError(e.response?.data?.detail || e.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
      setStatusMsg('');
    }
  };

  // ---------------- render ----------------

  const resolvedSpFolderCheck = spClient === '__new__' ? newSpClientName.trim() : spClient;
  const canSubmit = !loading &&
    resolvedSpFolderCheck &&
    (kbClientId === '__new__' ? newKbClientName.trim() : kbClientId) &&
    (spClient === '__new__' || (folderPath && filename.trim()));

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-cloud-upload me-2"></i>
              Publish to SharePoint — <span className="text-muted">{client.name}</span>
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>

          {/* Body */}
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger d-flex align-items-start gap-2">
                <i className="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            {/* SharePoint client folder selector */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                SharePoint Folder <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={spClient}
                onChange={(e) => handleSpClientChange(e.target.value)}
                disabled={loading}
              >
                <option value="">— Select SharePoint folder —</option>
                <option value="__new__">+ Create new SharePoint folder</option>
                {[...spClients].sort((a, b) => a.localeCompare(b)).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {spClient === '__new__' && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder='Folder name, e.g. "Schmitz, Greg"'
                  value={newSpClientName}
                  onChange={(e) => setNewSpClientName(e.target.value)}
                  disabled={loading}
                />
              )}
            </div>

            {/* Knowledgebase client selector */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Knowledgebase Client <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={kbClientId}
                onChange={handleKbClientChange}
                disabled={loading}
              >
                <option value="">— Select a client —</option>
                <option value="__new__">+ Create new knowledgebase client</option>
                {[...kbClients].sort((a, b) => a.client_name.localeCompare(b.client_name)).map(c => (
                  <option key={c.client_id} value={c.client_id}>
                    {c.client_name}
                  </option>
                ))}
              </select>
              {kbClientId === '__new__' && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder='Name, e.g. "Schmitz, Greg"'
                  value={newKbClientName}
                  onChange={(e) => setNewKbClientName(e.target.value)}
                  disabled={loading}
                />
              )}
              <div className="form-text">
                Select the matching knowledgebase client to link this file to their record.
              </div>
            </div>

            {/* Destination sub-folder selector */}
            {spClient !== '__new__' && (
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Destination Folder <span className="text-danger">*</span>
              </label>
              {loadingFolders ? (
                <div className="text-muted small py-2">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Loading SharePoint folders…
                </div>
              ) : (
                <select
                  className="form-select"
                  value={folderPath}
                  onChange={(e) => setFolderPath(e.target.value)}
                  disabled={loading || !spClient}
                >
                  <option value="">— Select a folder —</option>
                  {folders.map(f => (
                    <option key={f.full_path} value={f.full_path}>
                      {f.name}
                    </option>
                  ))}
                </select>
              )}
              {folderPath && (
                <div className="form-text text-muted font-monospace">
                  <i className="bi bi-folder2-open me-1"></i>
                  {folderPath}
                </div>
              )}
              {spClient && !loadingFolders && folders.length === 0 && (
                <div className="form-text text-warning">
                  No folders found for this client in SharePoint.
                </div>
              )}
            </div>
            )}
            {spClient === '__new__' && (
              <div className="alert alert-info d-flex align-items-start gap-2 mb-3">
                <i className="bi bi-info-circle-fill mt-1 flex-shrink-0"></i>
                <span>
                  A new SharePoint client folder and <strong>FP Docs</strong> subfolder will be created automatically on publish.
                </span>
              </div>
            )}

            {/* Filename */}
            <div className="mb-3">
              <label className="form-label fw-semibold">File Name</label>
              <input
                type="text"
                className="form-control font-monospace"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                disabled={loading}
              />
              <div className="form-text">
                Standard format: <code>Cashflow-Annual Cashflow Analysis-[Last Name]-[Year].xlsx</code>
              </div>
            </div>

            {/* Overwrite toggle */}
            <div className="mb-3 form-check">
              <input
                id="cf-overwrite"
                type="checkbox"
                className="form-check-input"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="cf-overwrite">
                Overwrite existing file if it already exists in SharePoint
              </label>
            </div>

            {/* Progress status */}
            {statusMsg && (
              <div className="alert alert-info d-flex align-items-center gap-2 mb-0">
                <span className="spinner-border spinner-border-sm flex-shrink-0"></span>
                <span>{statusMsg}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handlePublish}
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Publishing…
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-1"></i>
                  Publish to SharePoint
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
